import APIConfig from "service/network/APIConfig";
import APIService from "service/network/APIService";

/**
 * Medicine v2 API client (api-medicine-v2-service). Mirrors the CC v2 /
 * Diagnosis v2 clients.
 *
 * Contract notes:
 *
 * 1. Every mutating call needs `expectedVersion` (the last version the caller
 *    saw). On mismatch the server returns 409 with a fresh doc.
 *
 * 2. Actor is derived server-side from the auth headers phr already sends.
 *
 * 3. Status enum is `"ongoing" | "completed" | "discontinued"`. The
 *    update-status endpoint takes this string; the server flips EndedBy/At
 *    on the medicine record when the new status is completed or discontinued.
 *
 * 4. Dedup key on Add is `medicineCode.mCode` (from api-medicine-service
 *    catalog). Free-text medicines skip dedup — user can create duplicates.
 */
class MedicineService {
  constructor() {
    this.apiService = new APIService();
  }

  list(patientId, onResponse, onError) {
    const url = APIConfig.medicineV2.list.format(patientId);
    return this.apiService.get(url, onResponse, onError);
  }

  /**
   * Add a medicine. Body shape matches beans.AddRequest:
   *   { medicine, formulation, medicineCode?, diagnosisId?, sinceWhen,
   *     medStartDate, medEndDate?, ongoingIndefinitely, durationDays,
   *     doseQuantity, doseFrequency, instructions, description?, expectedVersion }
   */
  add(patientId, body, onResponse, onError) {
    const url = APIConfig.medicineV2.add.format(patientId);
    return this.apiService.post(url, body, onResponse, onError);
  }

  /**
   * Update status. Body shape matches beans.UpdateStatusRequest:
   *   { status, sinceWhen, medStartDate, medEndDate?, ongoingIndefinitely,
   *     durationDays, doseQuantity, doseFrequency, instructions,
   *     description?, expectedVersion }
   *
   * `status` drives the case:
   *   - "ongoing"      → regimen edit (case B) — the medicine keeps running
   *   - "completed"    → status flip; server sets EndedBy/At
   *   - "discontinued" → status flip; server sets EndedBy/At
   */
  updateStatus(patientId, medicineId, body, onResponse, onError) {
    const url = APIConfig.medicineV2.updateStatus.format(patientId, medicineId);
    return this.apiService.post(url, body, onResponse, onError);
  }

  /**
   * Soft-delete. Body carries `expectedVersion` for optimistic locking.
   */
  delete(patientId, medicineId, expectedVersion, onResponse, onError) {
    const url = APIConfig.medicineV2.delete.format(patientId, medicineId);
    return fetch(url, {
      method: "DELETE",
      headers: {
        "Content-Type": "application/json",
        client: this._client(),
        phone_no: this._header("phone_no"),
        auth_token: this._header("auth_token"),
      },
      body: JSON.stringify({ expectedVersion: expectedVersion || 0 }),
    })
      .then((r) => r.json().then((data) => (r.ok ? onResponse(data) : onError(data))))
      .catch((err) =>
        onError({ message: err && err.message ? err.message : "Delete failed" })
      );
  }

  trend(patientId, medicineId, onResponse, onError) {
    const url = APIConfig.medicineV2.trend.format(patientId, medicineId);
    return this.apiService.get(url, onResponse, onError);
  }

  /**
   * Append one AdherenceReview to the LATEST StatusEntry. Followup-only,
   * doesn't touch medicine.status. Body:
   *   { outcome, adherenceStart, adherenceEnd, description?, expectedVersion }
   */
  addAdherenceReview(patientId, medicineId, body, onResponse, onError) {
    const url = APIConfig.medicineV2.adherenceReview.format(patientId, medicineId);
    return this.apiService.post(url, body, onResponse, onError);
  }

  _header(name) {
    try {
      const user = JSON.parse(localStorage.getItem("zy.USER_PROFILE"));
      if (name === "phone_no") return (user && user.phoneno) || "";
      if (name === "auth_token") {
        return (user && user.accessToken && user.accessToken.accessToken) || "";
      }
    } catch (e) {
      /* localStorage unavailable — return empty and let the server 401 */
    }
    return "";
  }

  _client() {
    try {
      const user = JSON.parse(localStorage.getItem("zy.USER_PROFILE"));
      const isDoctor =
        user && user.doctorProfile && user.doctorProfile.id > 0 &&
        !(user.employeeProfile && user.employeeProfile.id > 0);
      return isDoctor ? "doctor_web" : "bridge_web";
    } catch (e) {
      return "bridge_web";
    }
  }
}

export default MedicineService;
